//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import React from "react";
import { Dropdown } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { OrganizationInfo as ApiOrganizationInfo, SetOrganizationBillingV2EnabledRequest as ApiSetOrganizationBillingV2EnabledRequest } from "../../api/lib";
import { Field, FieldContent as FC, FieldLabelWide as FL } from "../../ui/lib";
import { withRefresh } from "../../util/WithRefresh";
import CommentsPrompt from "../comments/CommentsPrompt";
import { CommentCreationArgs, ICommentPromptStateForIntegrations } from "../comments/CommentTypes";
import OrganizationDetailsField, { IOrganizationDetailsFieldProps, IOrganizationDetailsFieldState } from "./OrganizationDetailsField";

interface IBillingV2EnabledFieldViewArgs {
  organizationInfo: ApiOrganizationInfo;
  canSetBillingV2Enabled: boolean;
  onSetOrganizationBillingV2Enabled: (enabled: boolean) => void;
}

const BillingV2EnabledToggle = ({ ...args }: IBillingV2EnabledFieldViewArgs) => {
  const billing_v2_enabled = !!args.organizationInfo.billing_v2_enabled;
  return (
    <>
      <Field>
        <FL>BillingV2 (Everest &amp; Stripe)</FL>
        <FC>
          {billing_v2_enabled ? "Enabled" : "Disabled"}
          {args.canSetBillingV2Enabled && (
            <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
              <Dropdown.Menu>
                {!billing_v2_enabled && <Dropdown.Item text="Enable" onClick={() => args.onSetOrganizationBillingV2Enabled(true)} />}
                {billing_v2_enabled && <Dropdown.Item text="Disable" onClick={() => args.onSetOrganizationBillingV2Enabled(false)} />}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </FC>
      </Field>
    </>
  );
};

interface IBillingV2EnabledFieldProps extends IOrganizationDetailsFieldProps, CommentCreationArgs {}

interface IBillingV2EnabledFieldState extends IOrganizationDetailsFieldState {
  commentPrompt: ICommentPromptStateForIntegrations;
}

class BillingV2EnabledField extends OrganizationDetailsField<IBillingV2EnabledFieldProps, IBillingV2EnabledFieldState> {
  state: IBillingV2EnabledFieldState = {
    refreshNeeded: false,
    commentPrompt: {
      showCommentsPrompt: false,
      defaultCommentOnEvent: "",
      onCommentConfirmation: async () => {},
      onCancellingCommentAddition: () => {},
      loadingMessage: "",
    },
  };
  resetCommentPrompt = () => {
    this.setState({
      commentPrompt: {
        showCommentsPrompt: false,
        defaultCommentOnEvent: "",
        onCommentConfirmation: async () => {},
        onCancellingCommentAddition: () => {},
        loadingMessage: "",
      },
    });
  };

  onSetOrganizationBillingV2Enabled = async (enabled: boolean) => {
    this.setState({
      commentPrompt: {
        showCommentsPrompt: true,
        defaultCommentOnEvent: `Set organization to ${enabled ? "enable" : "disable"} BillingV2 (Everest & Stripe)`,
        onCancellingCommentAddition: this.resetCommentPrompt,
        loadingMessage: "Changing organization BillingV2 setting, please wait...",
        onCommentConfirmation: async () => {
          const req: ApiSetOrganizationBillingV2EnabledRequest = {
            organization_id: this.props.organization.id,
            billing_v2_enabled: enabled,
          };
          await apiClients.idashboardClient.SetOrganizationBillingV2Enabled(req);
          this.refreshOrganizationInfo();
        },
      },
    });
  };

  render() {
    const can_set_billing_v2_enabled = this.hasPermission("internal-dashboard.organization.set-billing-v2-enabled");

    return (
      <div>
        {!!this.state.commentPrompt.showCommentsPrompt && (
          <CommentsPrompt
            commentPrompt={{
              ...this.state.commentPrompt,
              handleAddComment: this.props.createComment,
              onClose: this.resetCommentPrompt,
            }}
          />
        )}
        <BillingV2EnabledToggle {...this} {...this.props} {...this.state} canSetBillingV2Enabled={can_set_billing_v2_enabled} />
      </div>
    );
  }
}

export default withRefresh()(BillingV2EnabledField);
